import type { MemberLoginResponse } from '@module/apis/client-side/member/type';
import type { ASPAPIURLVariants, IPresetResponse } from '../apis/client-side/preset/type';
import { aspAuthIframeBottomPaddingSafeLocalStorage } from '../lib/storage/storageImplementations';
import { plog } from '../service/pino';
import { ASPUtils } from '../utils/asp/asp-utils';
import { classifyASPHostname } from '../utils/site/hostname';
import {
  injectASPAuthToTanstackQuery,
  injectPresetToTanstackQuery,
  injectUserInfoToTanstackQuery,
  sendSyncXHR,
  updateSessionKey,
} from './setting.helpers';
import type { ASPConfig, ASPInfo, ASPInfoResponse, CoreConfig } from './type';

function parseBottomPadding(value: string | null) {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
}

/**
 * ASP (Application Service Provider) 부트스트랩 & 런타임 상태 관리자.
 *
 * ─── 도메인 용어 정리 ───────────────────────────────────────────────
 *
 *   "ASP" 는 외부 파트너가 우리 서비스를 자신의 사이트로 가져가는 진입
 *   방식을 통칭한다. 진입 방식은 두 가지로만 나뉜다:
 *
 *   ▸ iframe 타입
 *     - 파트너 페이지가 우리 페이지를 <iframe> 으로 직접 임베드.
 *     - 호스트:
 *         · wasp.pandalive.co.kr   / masp.pandalive.co.kr   (정식)
 *         · wasp-new.pandalive.co.kr / masp-new.pandalive.co.kr
 *           (배포 슬롯 — 정식과 동일한 로직/인증/비즈니스 처리)
 *     - 부트스트랩: GET https://apiasp.pandalive.co.kr/v1/asp/auth?type=iframe
 *     - 상태 보관 : `iframeASPConfig`
 *     - 식별 헬퍼: `aspCond.if('iframe-asp')`
 *
 *   ▸ popup 타입  (= "new" 타입)
 *     - 새 창 / 새 탭으로 띄우는 진입. "new" 는 시간적 "최신" 이 아니라
 *       **"새 창 (new window)" 의 줄임말**이다. 코드 전반에서
 *       `newASPPreset` / `aspCond.if('new-asp')` 로 등장하면 popup 타입을
 *       가리키는 것으로 읽는다.
 *     - 호스트 : livetv[숫자]?.{aspCode}.co.kr / mlivetv[숫자]?.{aspCode}.co.kr
 *     - 부트스트랩: GET /v1/config/preset → GET /v1/asp/auth?type=popup
 *     - 상태 보관 : `newASPPreset`
 *     - 식별 헬퍼: `aspCond.if('new-asp')`
 *
 *   호스트가 어느 분류에 속하는지의 단일 판정은
 *   `utils/site/hostname.ts` 의 `classifyASPHostname()` 이 담당한다.
 *   새 배포 슬롯이나 livetv 변형이 추가되면 그 파일의 정규식만 갱신하면 된다.
 * ─────────────────────────────────────────────────────────────────────
 */
export class ASPConfigManager {
  private coreConfig: CoreConfig;

  /** iframe 타입 ASP 의 부트스트랩 응답 (`/v1/asp/auth?type=iframe`). wasp/masp/-new 진입 시 채워진다. */
  public iframeASPConfig?: ASPConfig | null;

  /**
   * popup 타입 ASP (= "new" 타입) 의 preset 응답 (`/v1/config/preset`).
   * livetv / mlivetv 진입 시 채워진다. 필드명에 "new" 가 들어가는 이유는
   * popup 이 "새 창(new window)" 진입이라서이지, 시간적 "최신" 이 아니다.
   *
   * TODO: Remove IPresetResponse from here, since presetQueryHooks.ts handles it.
   */
  public newASPPreset: IPresetResponse | null = null;

  /** 현재 활성 ASP 타입에 맞는 부모창 메시지 브릿지. 부트스트랩 성공 시 생성. */
  public aspUtils: ASPUtils | null = null;

  /**
   * ASP 사이트 정보의 **단일 보관소**. 부트스트랩 후 채워진다.
   *
   * 값의 출처는 진입 타입에 따라 다르다:
   *   - iframe 타입 : `/v1/asp/auth` 응답의 `aspInfo` 를 그대로 사용
   *   - popup  타입 : `/v1/asp/info` 응답 (별도 호출)
   *
   * 모든 consumer 는 `aspInfoSettled()` 또는 이 필드를 직접 읽어 사용한다.
   */
  public aspInfo: ASPInfo | null = null;

  /**
   * 부트스트랩이 실제로 분기를 결정하기 전까지는 'none'. iframe/popup 둘 중
   * 하나만 신뢰할 수 있는 값이며, 'none' 일 때는 ASP 컨텍스트로 간주하면 안 된다.
   */
  public serviceType: 'popup' | 'iframe' | 'none' = 'none';

  constructor(coreConfig: CoreConfig) {
    this.coreConfig = coreConfig;
    this.initASPConfig();
  }

  private initASPConfig() {
    if (typeof window === 'undefined') return;

    const { hostname } = window.location;

    // 호스트 → 진입 타입 매핑은 utils/site/hostname.ts 한 곳에서 관리.
    //   iframe 타입 : wasp / masp / wasp-new / masp-new
    //   popup  타입 : livetv* / mlivetv*   (= "new" 타입, 새 창으로 진입)
    // 새 배포 슬롯이나 livetv 변형이 늘면 그 파일의 정규식만 갱신하면 된다.

    switch (classifyASPHostname(hostname)) {
      case 'iframe':
        this.initIframeASPConfig();
        return;
      case 'popup':
        this.initPopupASPConfig(hostname);
        return;
      default:
        plog.info({
          name: 'ASPConfigManager',
          msg: 'Unrecognized service hostname for ASP configuration',
          hostname,
        });
    }
  }

  /**
   * iframe 타입 ASP 부트스트랩.
   *
   * 호출 시점: 호스트가 wasp / masp / wasp-new / masp-new 일 때.
   * 흐름     : `/v1/asp/auth` 호출 → 세션 키 세팅 → 응답의 `aspInfo` 로 ASPUtils 생성.
   *            (popup 분기와 달리 별도의 `/v1/asp/info` 호출은 하지 않는다.)
   */
  private initIframeASPConfig() {
    const iframeUrl = new URL(window.location.href);
    const aspCode = iframeUrl.searchParams.get('aspCode');
    const token = iframeUrl.searchParams.get('token');
    const type = iframeUrl.searchParams.get('type');
    const state = iframeUrl.searchParams.get('state');

    const iframeAuthURL = new URL(`${window.location.protocol}//apiasp.pandalive.co.kr/v1/asp/auth`);
    iframeAuthURL.searchParams.append('aspCode', aspCode || '');
    iframeAuthURL.searchParams.append('token', token || '');
    iframeAuthURL.searchParams.append('type', type || 'iframe');
    if (state) iframeAuthURL.searchParams.append('state', state);

    const aspConfig = sendSyncXHR(iframeAuthURL) as ASPConfig;
    if (aspConfig?.errorData) {
      injectASPAuthToTanstackQuery(aspConfig);
      this.handleASPAuthError(aspConfig.message);
      return;
    }

    if (!aspConfig?.result) {
      this.handleASPAuthError(aspConfig?.message);
      return;
    }

    updateSessionKey(aspConfig.loginInfo?.sessKey, aspConfig.loginInfo?.userInfo?.idx);

    this.iframeASPConfig = aspConfig;
    this.coreConfig.updateAPIURL('apiasp.pandalive.co.kr' as ASPAPIURLVariants);
    injectUserInfoToTanstackQuery(aspConfig.loginInfo);
    this.serviceType = 'iframe';

    // iframe 분기는 /v1/asp/auth 응답의 aspInfo 를 그대로 신뢰한다.
    // (popup 분기와 달리 별도의 /v1/asp/info 호출을 하지 않는다 — 백엔드 미구현으로
    //  /v1/asp/info → handleASPAuthError → '/asp/play' → 재 부트스트랩 → /v1/asp/info …
    //  의 무한 리다이렉트 루프가 발생했음. iframe 만 rollback.)
    if (aspConfig.aspInfo) {
      this.aspInfo = aspConfig.aspInfo;
      this.aspUtils = new ASPUtils(aspConfig.aspInfo, 'iframe', this.coreConfig);
    }
  }

  private getLoginInfo() {
    const loginInfoURL = new URL(this.coreConfig.API_URL);
    loginInfoURL.pathname = '/v1/member/login_info';
    const loginInfo = sendSyncXHR(loginInfoURL) as MemberLoginResponse;
    if (loginInfo) {
      updateSessionKey(loginInfo.loginInfo.sessKey, loginInfo.loginInfo.userInfo?.idx);
      injectUserInfoToTanstackQuery(loginInfo.loginInfo);
    }

    return loginInfo;
  }

  /**
   * `GET /v1/asp/info` — popup 분기 전용 aspInfo 소스.
   *
   * iframe 분기는 `/v1/asp/auth` 응답의 aspInfo 를 직접 쓰고 이 함수를 호출하지 않는다.
   * (iframe 에서는 /v1/asp/info 백엔드 미구현으로 인한 redirect 루프를 회피하기 위해 우회.)
   *
   * 호출 전제 : `/v1/asp/auth` 가 성공해서 `sessKeyAsp` 쿠키가 살아 있어야 한다.
   *            (서버는 session 으로부터 aspCode 를 도출하므로 별도 파라미터를 받지 않는다.)
   *
   * 사용 호스트는 `coreConfig.API_URL` — popup 부트스트랩 직후엔 파트너별 `preset.urlApi`.
   *
   * 성공 시 `this.aspInfo` 채우고 `true`. 실패하면 `handleASPAuthError` 호출 후 `false`.
   * 호출부는 반환값이 `false` 면 즉시 return 하고, `true` 면 곧바로 `ASPUtils` 를 만든다.
   */
  private fetchAspInfo(): boolean {
    const infoURL = new URL(this.coreConfig.API_URL);
    infoURL.pathname = '/v1/asp/info';
    const res = sendSyncXHR(infoURL) as ASPInfoResponse;

    if (!res?.result || !res.aspInfo) {
      this.handleASPAuthError(res?.message);
      return false;
    }

    this.aspInfo = res.aspInfo;
    return true;
  }

  private createPopupAuthParams() {
    const popupASPURL = new URL(window.location.href);
    const token = popupASPURL.searchParams.get('token');
    const state = popupASPURL.searchParams.get('state');
    if (!token) return null;

    return {
      popupASPURL,
      token,
      state,
      aspCode: popupASPURL.searchParams.get('aspCode'),
      path: popupASPURL.searchParams.get('path') || '',
      bottomPadding: popupASPURL.searchParams.get('bp'),
    };
  }

  private redirectIfGuestIsNotAllowed() {
    if (this.newASPPreset?.aspInfo?.link?.guestOut) {
      window.location.replace(this.newASPPreset.aspInfo.link.logout);
    }
  }

  /**
   * popup 타입 ASP (= "new" 타입) 부트스트랩.
   * 호출 시점: 호스트가 livetv* / mlivetv* 일 때.
   * 흐름: preset 로드 → 로그인 정보 조회 → URL 의 `token` 이 있으면
   *       팝업 인증 핸드셰이크, 없으면 기존 로그인 상태로 진입.
   */
  private initPopupASPConfig(hostname: string) {
    this.initPresetConfig(hostname);

    const loginInfo = this.getLoginInfo();

    const popupAuthParams = this.createPopupAuthParams();

    if (popupAuthParams) {
      // URL 에 token 이 있는 경우 팝업 인증 시도 — 성공 시 handleSuccessfulAuth 가
      // /v1/asp/info 를 받아 aspInfo + ASPUtils 까지 세팅한다.
      this.handlePopupAuthentication(popupAuthParams);
    } else if (loginInfo.loginInfo.userInfo.isLogin) {
      // 이미 sessKeyAsp 가 살아있는 경우 — 별도 인증 없이 바로 /v1/asp/info 로 aspInfo 확보.
      if (this.fetchAspInfo()) {
        this.aspUtils = new ASPUtils(this.aspInfo as ASPInfo, 'popup', this.coreConfig);
      }
      this.handleASPAuthSuccess();
    } else {
      this.redirectIfGuestIsNotAllowed();
    }
  }

  private initPresetConfig(hostname: string) {
    let configPresetEndpointAPIURL = new URL(`${window.location.protocol}//apiasp.pandalive.co.kr/v1/config/preset`);

    if (hostname.endsWith('smartfile.co.kr')) {
      // Smartfile WebView blocks cleartext to *.pandalive.co.kr — route via its own TLD instead.
      // 스마트파일은 clearText 차단 → apiasp.pandalive.co.kr 대신 apilivetv.smartfile.co.kr 로 우회한다.
      configPresetEndpointAPIURL = new URL(`${window.location.protocol}//apilivetv.smartfile.co.kr/v1/config/preset`);
    }
    // popup 분기는 livetv / mlivetv 변형만 도달하므로 hostname 파라미터는 항상 첨부.
    // dev/local prefix 는 backend 가 알아볼 수 있는 정식 호스트로 정규화한다.
    configPresetEndpointAPIURL.searchParams.append('hostname', hostname.replace(/^(?:local\.|dev\.)/, ''));
    const siteInfo = sendSyncXHR(configPresetEndpointAPIURL) as IPresetResponse;

    if (siteInfo?.preset) {
      injectPresetToTanstackQuery(siteInfo, hostname);
      this.newASPPreset = siteInfo;
      this.coreConfig.updateAPIURL(siteInfo.preset.urlApi);
      this.serviceType = 'popup';
      return;
    }

    throw new Error(
      'Failed to retrieve ASP preset configuration' +
        JSON.stringify({
          hostname: configPresetEndpointAPIURL.hostname,
          path: configPresetEndpointAPIURL.pathname,
        }),
    );
  }

  private extractASPCode(url: URL): string {
    // 정상 케이스 (livetv/mlivetv.{aspCode}.co.kr) 에서는 두 번째 라벨이 aspCode.
    // 부트스트랩 중 throw 하면 SettingConfig 생성 자체가 죽으므로 실패 시 '' 반환.
    // 호출부 (handlePopupAuthentication) 는 빈 문자열일 때 aspCode 파라미터를 생략한다.
    const hostParts = url.hostname.split('.');
    if (hostParts.length >= 3) return hostParts[1];

    plog.info({
      name: 'ASPConfigManager',
      msg: 'Failed to extract ASP code from the domain',
      hostname: url.hostname,
      path: url.pathname,
    });
    return '';
  }

  private handlePopupAuthentication({
    popupASPURL,
    token,
    state,
    aspCode: queryAspCode,
    path,
    bottomPadding,
  }: {
    popupASPURL: URL;
    token: string;
    state: string | null;
    aspCode: string | null;
    path: string;
    bottomPadding: string | null;
  }) {
    const aspCode = queryAspCode || this.extractASPCode(popupASPURL);
    const aspAuthURL = new URL(this.coreConfig.API_URL);
    aspAuthURL.pathname = '/v1/asp/auth';

    if (aspCode) aspAuthURL.searchParams.append('aspCode', aspCode);
    aspAuthURL.searchParams.append('token', token);
    if (state) aspAuthURL.searchParams.append('state', state);
    aspAuthURL.searchParams.append('type', 'popup');
    if (this.coreConfig.environment.startsWith('m-')) aspAuthURL.searchParams.append('browser', 'mobile');

    if (bottomPadding !== null) {
      aspAuthIframeBottomPaddingSafeLocalStorage.set(parseBottomPadding(bottomPadding));
    }

    const aspAuthRes = sendSyncXHR(aspAuthURL) as ASPConfig;

    if (aspAuthRes?.errorData) injectASPAuthToTanstackQuery(aspAuthRes);
    // ASPUtils 는 /v1/asp/info 응답을 받은 뒤 handleSuccessfulAuth 에서 생성된다.
    this.processASPAuthResponse(aspAuthRes, aspCode, path);
  }

  private processASPAuthResponse(aspAuthRes: ASPConfig, aspCode: string, path: string) {
    if (aspAuthRes?.result) {
      this.handleSuccessfulAuth(aspAuthRes, aspCode, path);
    } else if (aspAuthRes) {
      this.handleFailedAuth(aspAuthRes);
    } else {
      this.handleASPAuthError();
    }
  }

  private handleSuccessfulAuth(aspAuthRes: ASPConfig, aspCode: string, path: string) {
    if (aspAuthRes.loginInfo) {
      updateSessionKey(aspAuthRes.loginInfo.sessKey, aspAuthRes.loginInfo.userInfo?.idx);
      injectUserInfoToTanstackQuery(aspAuthRes.loginInfo);
    }

    // 인증 성공 후 세션 쿠키로 /v1/asp/info 호출 → aspInfo 단일 소스 채우고 ASPUtils 생성.
    if (!this.fetchAspInfo()) return;
    this.aspUtils = new ASPUtils(this.aspInfo as ASPInfo, 'popup', this.coreConfig);

    // 파트너가 광고 스크립트 주입을 요구한 경우 그대로 삽입.
    if (this.newASPPreset?.preset?.advertJs && aspCode) {
      const script = document.createElement('script');
      script.src = `${this.newASPPreset.preset.advertJs}&aspCode=${aspCode}`;
      document.head.appendChild(script);
    }

    this.handleASPAuthSuccess(path);
  }

  private async handleFailedAuth(aspAuthRes: ASPConfig) {
    await this.emitNoAccess(aspAuthRes.message || '알 수 없는 이유로 인증에 실패했습니다.');

    if (aspAuthRes.errorData?.aspExit !== 'Y') {
      this.handleASPAuthError(aspAuthRes.message);
      return;
    }

    // aspExit === 'Y' : 파트너 측 logout URL → 뒤로가기 → opener close 순서로 폴백.
    if (aspAuthRes.aspInfo?.link?.logout) {
      window.location.href = aspAuthRes.aspInfo.link.logout;
    } else if (history.length > 1) {
      window.history.back();
    } else if (window.opener) {
      window.close();
    } else {
      this.handleASPAuthError(aspAuthRes.message);
    }
  }

  private async emitNoAccess(message: string) {
    return new Promise<void>((resolve) => {
      const start = Date.now();
      // checker 는 listener(ASPRouteGuard) 가 모달 mount/dismiss 상태를 되돌려주는 슬롯.
      const checker = {
        closed: false,
        arrived: false,
        aspUtils: this.aspUtils,
      };
      const id = setInterval(() => {
        if (checker.arrived && Date.now() - start > 5000) {
          clearInterval(id);
          resolve();
        }
        if (checker.closed) resolve();
        this.aspUtils?.emit('no-access', message, checker);
      }, 400);
    });
  }

  private async emitNavigate(path: string) {
    return new Promise<void>((resolve, reject) => {
      const start = Date.now();
      const id = setInterval(() => {
        // popup(newtype) /auth 승인 직후의 네비게이션은 history 를 replace 한다.
        // (back 버튼이 /auth 로 되돌아가지 않도록.) 다른 emit 경로 (예: iframe B 의
        // goCharge → /asp/charge) 는 옵션 없이 emit 하므로 push 가 유지된다.
        this.aspUtils?.emit('navigate', path, { replace: true });
        if (window.location.pathname.includes(path)) {
          clearInterval(id);
          resolve();
        }

        if (Date.now() - start > 5000) {
          clearInterval(id);
          reject(new Error(`Navigation to ${path} failed after 5 seconds`));
        }
      }, 400);
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // Runtime context : 우리 페이지가 어떤 컨테이너 안에서 실행 중인지.
  //   - iframe          : <iframe> 으로 임베드 (wasp/masp 류).
  //   - popup-same-site : 새 창 + opener 가 같은 도메인 (= 레거시 'site').
  //   - popup-cross     : 새 창 + opener 가 다른 도메인 (= 레거시 'asp').
  //   - standalone      : top-level 직접 진입 + opener 없음 (= 레거시 'none').
  // ───────────────────────────────────────────────────────────────────
  private detectRuntimeContext(): 'iframe' | 'popup-same-site' | 'popup-cross' | 'standalone' {
    if (typeof window === 'undefined') return 'standalone';
    if (window.self !== window.top) return 'iframe';
    if (!window.opener) return 'standalone';

    try {
      const opener = window.opener as Window;
      // opener URL 이 자기 자신과 같으면 의미 있는 opener 가 아님 → standalone.
      if (opener.location.href === window.location.href) return 'standalone';
      if (opener.location.host === window.location.host) return 'popup-same-site';
      return 'popup-cross';
    } catch {
      // cross-origin opener.location 접근은 SecurityError → cross 로 단정.
      return 'popup-cross';
    }
  }

  /** same-site popup 공통 마무리 : opener 새로고침 후 자기 창 닫기. */
  private closeAndReloadOpener(): void {
    if (typeof window === 'undefined' || !window.opener) return;
    window.opener.location.reload();
    window.close();
  }

  /** ASP 인증 성공 후 후처리 — 컨텍스트별로 안전한 다음 화면으로 사용자를 보낸다. */
  private handleASPAuthSuccess(path?: string) {
    const targetPath = path || '/live';

    if (this.detectRuntimeContext() === 'popup-same-site') {
      this.closeAndReloadOpener();
      return;
    }

    // iframe / cross-popup / standalone : /auth 랜딩에 머물러 있을 때만
    // 내부 네비게이션 (다른 페이지에 이미 들어와 있으면 페이지 자체 로직에 맡김).
    if (window.location.pathname.startsWith('/auth')) {
      this.emitNavigate(targetPath);
    }
  }

  /** ASP 인증 실패 후 후처리 — 컨텍스트별로 안전한 곳으로 사용자 보내기. */
  private handleASPAuthError(message?: string) {
    console.error('ASPConfigManager::handleASPAuthError', message ?? '');

    const ctx = this.detectRuntimeContext();

    // same-site popup : 성공 케이스와 동일하게 opener 로 돌려보낸다.
    if (ctx === 'popup-same-site') {
      this.closeAndReloadOpener();
      return;
    }

    // popup / standalone : preset 에 파트너 로그인 URL 이 있으면 그쪽으로 이동.
    if (ctx !== 'iframe' && this.newASPPreset?.aspInfo?.link?.login) {
      window.location.href = this.newASPPreset.aspInfo.link.login;
      return;
    }

    // iframe 폴백 : ASP 라우트 내부의 lobby 로 보낸다.
    // 레거시 '/live' 는 wasp 호스트에서 미들웨어가 '/asp/live' 로 리라이트하지만
    // pandatv-pc 에 해당 라우트가 없어 404. '/asp/play' 가 정식 lobby.
    if (ctx === 'iframe') {
      window.location.href = '/asp/play';
    }
  }

  /**
   * 외부에서 `aspInfo` 를 비동기로 받아갈 때 쓰는 API 안정성용 래퍼.
   * 부트스트랩은 모두 동기이므로 호출 시점엔 이미 settle 되어 있다.
   */
  async aspInfoSettled(): Promise<ASPInfo | null> {
    return this.aspInfo;
  }
}

/**

 const url = new URL(location.href);
  url.hostname = "local.wasp.pandalive.co.kr";
  location.replace(url.toString());

  */
